import { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';

interface AffectedWorker {
  name: string;
  runningTasks: number;
}

interface UpgradeWorkerDialogProps {
  nodeAgentId: string;
  instanceId: string;
  workerName: string;
  currentPackageName: string;
  currentVersion?: string;
  affectedWorkers?: AffectedWorker[];
  onClose: () => void;
  onConfirm: () => void;
}

export default function UpgradeWorkerDialog({
  nodeAgentId,
  instanceId,
  workerName,
  currentPackageName,
  currentVersion,
  affectedWorkers = [],
  onClose,
  onConfirm,
}: UpgradeWorkerDialogProps) {
  const [newVersion, setNewVersion] = useState(currentVersion || '');

  const totalRunningTasks = affectedWorkers.reduce((sum, w) => sum + w.runningTasks, 0);
  const busyWorkers = affectedWorkers.filter((w) => w.runningTasks > 0);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await sendNodeCommand(nodeAgentId, 'upgrade_worker', {
        instance_id: instanceId,
        package_version: newVersion.trim() || undefined,
      });
      onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to upgrade worker:', err);
      setError('Failed to upgrade worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upgrade Worker</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          Upgrade worker <strong>{workerName}</strong>.
          <br />
          All workers sharing this working directory will be restarted.
        </DialogContentText>
        {totalRunningTasks > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {totalRunningTasks} running {totalRunningTasks === 1 ? 'task' : 'tasks'}
            {busyWorkers.length > 1 && (
              <> ({busyWorkers.map((w) => `${w.name} (${w.runningTasks})`).join(', ')})</>
            )}{' '}
            will be interrupted: the upgrade restarts workers immediately without draining. Interrupted tasks are
            flagged as lost and only run again after being reset.
          </Alert>
        )}
        <TextField label="Package Name" value={currentPackageName} fullWidth margin="normal" disabled />
        <TextField
          label="New Version"
          value={newVersion}
          onChange={(e) => setNewVersion(e.target.value)}
          fullWidth
          margin="normal"
          placeholder="e.g. 0.12.0"
          helperText="Leave empty to upgrade to the latest version."
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Upgrading\u2026' : 'Upgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
